/**
 * Contract Intelligence "Procurement method" column and Framework filter (2026-10-13).
 *
 * Method = closed_deals.deal_type (a controlled English vocabulary). Framework = the buying route
 * the API resolved from framework_name + framework_id against `route_families`
 * (api/src/services/routeFamilies.ts), returned as framework_route_type / framework_routes.
 *
 * 🔴 framework_name is NOT always a framework. In Chile, Peru, Spain, Portugal and France it holds
 * the legal PROCEDURE ("Trato Directo", "Procédure ouverte") on ~21,000 visible deals; the API tags
 * those route_type 'procedure', and they are never shown as a framework here. When deal_type is
 * blank, the procedure name stands in as the method instead.
 */

const METHOD_LABELS = {
  contract: 'Contract',
  sole_source: 'Sole source',
  competitive_award: 'Competitive award',
  purchase_order: 'Purchase order',
  grant: 'Grant',
  cooperative_contract: 'Cooperative contract',
  blanket_purchase_agreement: 'Blanket purchase agreement',
  task_order: 'Task order',
  amendment: 'Amendment',
  license_agreement: 'Licence agreement',
  intergovernmental_agreement: 'Intergovernmental agreement',
};

function methodLabel(dealType) {
  const k = String(dealType ?? '').trim();
  if (!k) return null;
  if (METHOD_LABELS[k]) return METHOD_LABELS[k];
  const h = k.replace(/_/g, ' ');
  return h.charAt(0).toUpperCase() + h.slice(1);
}

/** The framework names a row belongs to (column and filter): resolved buying routes only. */
export function frameworkNamesOf(deal) {
  if (!deal) return [];
  const type = deal.framework_route_type;
  if (type === 'procedure') return [];
  // [2026-10-13] RESOLVED ROUTES ONLY. The unresolved ~1% of framework_name is mostly not a
  // framework at all — Czech contract subjects, French notice ids, the literal "True", buyer
  // names — and printing it raw filled the dropdown with noise (Tiago). A real framework that is
  // missing belongs in route_families, not here.
  if (Array.isArray(deal.framework_routes) && deal.framework_routes.length) return deal.framework_routes;
  return deal.framework_route ? [deal.framework_route] : [];
}

/** { method, framework, frameworkTitle } for one row. */
export function procurementMethodDisplay(deal) {
  if (!deal) return { method: null, framework: null, frameworkTitle: null };
  const rawName = String(deal.framework_name ?? '').trim();
  let method = methodLabel(deal.deal_type);
  if (!method && deal.framework_route_type === 'procedure') method = deal.framework_route || rawName || null;
  const names = frameworkNamesOf(deal);
  const framework = names.length ? names.join(' · ') : null;
  // The original text on hover, when the display shows the normalised name instead.
  const frameworkTitle = framework && rawName && rawName !== framework ? rawName : null;
  return { method, framework, frameworkTitle };
}

/**
 * Live Radar "Type" column (2026-10-17).
 *
 * The LIVE table (`deals`) has its OWN deal_type vocabulary — §4 of the procure_agent README
 * (RFP / RFQ / RFI / Framework / Framework Call-Off / Sole Source / Direct Award / Direct Renewal /
 * Pilot / Trial / Grant-Funded Procurement) — NOT closed_deals' §0.9. Those values are already
 * human-readable, so METHOD_LABELS misses them and `methodLabel`'s humaniser passes them through
 * unchanged, which is what we want.
 *
 * 🔴 Framework source differs from Contract Intelligence on purpose. CI prints only API-resolved
 * routes because raw closed_deals.framework_name was noise. `deals.framework_name` was normalised
 * on 2026-10-17 to 159 canonical vehicles, with purchase-order numbers, an accounting standard and
 * misfiled procedure labels cleared out, so printing it is safe here. Resolved routes still win
 * when the API supplies them.
 */
export function liveDealTypeDisplay(deal) {
  if (!deal) return { method: null, framework: null, frameworkTitle: null };
  const method = methodLabel(deal.deal_type);
  const resolved = frameworkNamesOf(deal);
  const raw = String(deal.framework_name ?? '').trim();
  const framework = resolved.length ? resolved.join(' \u00b7 ') : (raw || null);
  const frameworkTitle = resolved.length && raw && raw !== framework ? raw : null;
  return { method, framework, frameworkTitle };
}
